import type { StoredUserConfig } from '../types'
import { ParentMessageData } from './post-message-to-parent'
import { WidgetDOM } from './widget-dom'
import { getUserConfig } from '../user-config'
import { fireCloseListeners, fireMountListeners } from '../listeners'

type OpenSurveyData = {
  surveyId: string
  showInitialLoading?: boolean
  executedFrom: 'code' | 'auto'
  openingQuestionId?: string
  autoFocus?: boolean
}

export type FrameMessageData =
  | ({ type: 'specific.widget.open' } & OpenSurveyData)
  | ({
      type: 'specific.widget.initialize'
      appId: string
      windowWidth: number
      frameId: string
    } & StoredUserConfig)
  | ({
      type: 'specific.widget.user'
    } & StoredUserConfig)
  | {
      type: 'specific.widget.window-width'
      width: number
    }

type Config = {
  initializingFrames: {
    [id: string]: WidgetDOM | undefined
  }
  frames: {
    [id: string]: WidgetDOM | undefined
  }
  messages: Array<{ frameId: string; data: FrameMessageData }>
}

const config: Config = {
  initializingFrames: {},
  frames: {},
  messages: [],
}

export const updateIframesColorScheme = () => {
  for (const frameId in config.initializingFrames) {
    const frame = config.initializingFrames[frameId]

    if (frame) {
      frame.updateIframeColorScheme()
    }
  }

  for (const frameId in config.frames) {
    const frame = config.frames[frameId]

    if (frame) {
      frame.updateIframeColorScheme()
    }
  }
}

export const openSurvey = ({
  appId,
  frameId,
  forceOpen = false,
  file,
  ...surveyOpenData
}: {
  appId: string
  frameId: string
  forceOpen?: boolean
  file?: string
} & OpenSurveyData) => {
  const currentFrame =
    config.initializingFrames[frameId] || config.frames[frameId]

  if (!forceOpen && currentFrame) {
    return
  }

  if (currentFrame) {
    postMessageToFrame(frameId, {
      type: 'specific.widget.open',
      ...surveyOpenData,
    })

    return
  }

  const userConfig = getUserConfig(appId)
  const data = new WidgetDOM({
    visible: surveyOpenData.showInitialLoading,
    file,
  })
  config.initializingFrames[frameId] = data

  data.registerLoadListener(() => {
    setFrameMessagesListener({
      frameId,
    })

    postDirectMessageToFrame(data.iframe, {
      type: 'specific.widget.initialize',
      appId,
      windowWidth: window.innerWidth,
      frameId,
      ...userConfig,
    })

    postMessageToFrame(frameId, {
      type: 'specific.widget.open',
      ...surveyOpenData,
    })
  })

  data.registerResizeListener(() => {
    postMessageToFrame(frameId, {
      type: 'specific.widget.window-width',
      width: window.innerWidth,
    })
  })
}

const postDirectMessageToFrame = (
  frame: HTMLIFrameElement,
  data: FrameMessageData
) => {
  frame.contentWindow?.postMessage(data, '*')
}

export const postMessageToFrame = (frameId: string, data: FrameMessageData) => {
  if (config.frames[frameId]) {
    postDirectMessageToFrame(config.frames[frameId].iframe, data)
  } else {
    config.messages.push({
      frameId,
      data,
    })
  }
}

const setFrameReady = (frameId: string) => {
  const initFrame = config.initializingFrames[frameId]

  if (!initFrame) {
    return
  }

  config.frames[frameId] = initFrame
  config.initializingFrames[frameId] = undefined
  config.messages = config.messages.filter(m => {
    postMessageToFrame(frameId, m.data)
    return m.frameId !== frameId
  })
}

const setFrameMessagesListener = ({ frameId }: { frameId: string }) => {
  let oldOverflowY: string | undefined

  window.addEventListener('message', e => {
    if (!e?.data) {
      return
    }

    const data = e?.data as ParentMessageData

    if (data.frameId !== frameId) {
      return
    }

    switch (data.type) {
      case 'specific.widget.mounted': {
        setFrameReady(frameId)
        fireMountListeners(frameId)
        break
      }
      case 'specific.widget.config': {
        const frame = config.frames[frameId]

        if (frame) {
          frame.updateVariant(data.variant)
          frame.updateVisibility(true)
        }
        break
      }
      case 'specific.widget.set-height': {
        const frame = config.frames[frameId]

        if (frame) {
          frame.updateHeight(data.height)
        }
        break
      }

      case 'specific.widget.closed': {
        if (oldOverflowY !== undefined) {
          window.document.body.style.overflowY = oldOverflowY
        }

        const frame = config.frames[frameId]

        if (frame) {
          frame.destroy()
          document.body.removeChild(frame.wrapper)
          config.frames[frameId] = undefined
        }

        fireCloseListeners(frameId)
        break
      }

      case 'specific.widget.first-touch': {
        const frame = config.frames[frameId]

        if (frame) {
          frame.setTouched()
        }

        break
      }
    }
  })
}
